import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft,
  Users,
  UserPlus,
  Heart,
  Star,
  Sparkles,
  Check,
  X,
  Loader2,
  Mail,
  ChevronRight,
  Trash2,
  MessageCircle,
  Rss,
  Trophy,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const FriendsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("friends");
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addEmail, setAddEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [compatDialog, setCompatDialog] = useState(null);
  const [compatLoading, setCompatLoading] = useState(false);

  useEffect(() => {
    fetchFriends();
    fetchRequests();
  }, []);

  useEffect(() => {
    if (activeTab === "feed") fetchFeed();
  }, [activeTab]);

  const fetchFriends = async () => {
    try {
      const res = await api.get("/social/friends");
      setFriends(res.data);
    } catch (e) {} finally { setLoading(false); }
  };

  const fetchRequests = async () => {
    try {
      const res = await api.get("/social/friends/requests");
      setRequests(res.data);
    } catch (e) {}
  };

  const fetchFeed = async () => {
    try {
      const res = await api.get("/social/friends/feed");
      setFeed(res.data);
    } catch (e) {}
  };

  const sendRequest = async (e) => {
    e.preventDefault();
    if (!addEmail.trim()) return;
    setSending(true);
    try {
      const res = await api.post("/social/friends/request", { email: addEmail.trim() });
      toast.success(res.data.message);
      setAddEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send request");
    } finally { setSending(false); }
  };

  const respondRequest = async (friendshipId, action) => {
    try {
      const res = await api.post("/social/friends/respond", { friendship_id: friendshipId, action });
      toast.success(res.data.message);
      fetchRequests();
      fetchFriends();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const removeFriend = async (friendshipId, friendName) => {
    if (!window.confirm(`Remove ${friendName} from friends?`)) return;
    try {
      await api.delete(`/social/friends/${friendshipId}`);
      toast.success("Friend removed");
      fetchFriends();
    } catch (err) {
      toast.error("Failed to remove friend");
    }
  };

  const viewCompatibility = async (friendId) => {
    setCompatLoading(true);
    setCompatDialog({ loading: true });
    try {
      const res = await api.get(`/social/friends/${friendId}/compatibility`);
      setCompatDialog(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load compatibility");
      setCompatDialog(null);
    } finally { setCompatLoading(false); }
  };

  const tabs = [
    { id: "friends", label: "Friends", icon: <Users className="w-4 h-4" /> },
    { id: "requests", label: `Requests${requests.length ? ` (${requests.length})` : ""}`, icon: <UserPlus className="w-4 h-4" /> },
    { id: "feed", label: "Feed", icon: <Rss className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen pb-24" style={{ background: "#070B14" }}>
      {/* Header */}
      <div className="sticky top-0 z-30 backdrop-blur-xl bg-[#070B14]/80 border-b border-white/5">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="text-white/60 hover:text-white" data-testid="friends-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-display text-xl text-white">Friends</h1>
          <button
            onClick={() => navigate("/leaderboard")}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all text-sm font-body"
            data-testid="leaderboard-link"
          >
            <Trophy className="w-4 h-4" />
            Leaderboard
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Add Friend Form */}
        <form onSubmit={sendRequest} className="glass-card p-4 flex gap-3" data-testid="add-friend-form">
          <Input
            type="email"
            placeholder="Friend's email address..."
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
            className="custom-input flex-1"
            data-testid="add-friend-email"
          />
          <Button type="submit" disabled={sending} className="btn-gold px-4" data-testid="add-friend-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
          </Button>
        </form>

        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 rounded-xl p-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`friends-tab-${tab.id}`}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-body text-sm transition-all ${
                activeTab === tab.id
                  ? "bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30"
                  : "text-white/50 hover:text-white/70"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Friends List */}
        {activeTab === "friends" && (
          <div className="space-y-3" data-testid="friends-list">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37] mx-auto" />
              </div>
            ) : friends.length === 0 ? (
              <div className="text-center py-12 glass-card p-8">
                <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/60 font-body">No friends yet</p>
                <p className="text-white/40 font-body text-sm mt-1">Add friends by email to compare compatibility</p>
              </div>
            ) : (
              friends.map((friend) => (
                <div key={friend.friendship_id} className="glass-card p-4 flex items-center gap-4" data-testid={`friend-card-${friend.friend_id}`}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8B5CF6]/30 to-[#D4AF37]/20 flex items-center justify-center text-white font-display text-lg">
                    {(friend.friend_name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-body font-medium truncate">{friend.friend_name}</p>
                    <p className="text-white/40 text-xs font-body">{friend.friend_email}</p>
                  </div>
                  <button
                    onClick={() => viewCompatibility(friend.friend_id)}
                    className="p-2 rounded-lg bg-[#8B5CF6]/10 text-[#C4B5FD] hover:bg-[#8B5CF6]/20 transition-all"
                    data-testid={`compat-btn-${friend.friend_id}`}
                    title="View Compatibility"
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => removeFriend(friend.friendship_id, friend.friend_name)}
                    className="p-2 rounded-lg bg-red-500/10 text-red-400/60 hover:text-red-400 hover:bg-red-500/20 transition-all"
                    data-testid={`remove-friend-${friend.friend_id}`}
                    title="Remove Friend"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Friend Requests */}
        {activeTab === "requests" && (
          <div className="space-y-3" data-testid="requests-list">
            {requests.length === 0 ? (
              <div className="text-center py-12 glass-card p-8">
                <Mail className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/60 font-body">No pending requests</p>
              </div>
            ) : (
              requests.map((req) => (
                <div key={req.friendship_id} className="glass-card p-4 flex items-center gap-4" data-testid={`request-card-${req.friendship_id}`}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37]/30 to-[#8B5CF6]/20 flex items-center justify-center text-white font-display text-lg">
                    {(req.user_name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-body font-medium truncate">{req.user_name}</p>
                    <p className="text-white/40 text-xs font-body">{req.user_email}</p>
                  </div>
                  <button
                    onClick={() => respondRequest(req.friendship_id, "accept")}
                    className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-all"
                    data-testid={`accept-${req.friendship_id}`}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => respondRequest(req.friendship_id, "reject")}
                    className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                    data-testid={`reject-${req.friendship_id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Friends Feed */}
        {activeTab === "feed" && (
          <div className="space-y-3" data-testid="friends-feed">
            {feed.length === 0 ? (
              <div className="text-center py-12 glass-card p-8">
                <Rss className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/60 font-body">No shared readings from friends yet</p>
                <p className="text-white/40 font-body text-sm mt-1">When your friends share their wisdom, it will appear here</p>
              </div>
            ) : (
              feed.map((item, i) => (
                <div key={item.share_id || i} className="glass-card p-4 space-y-2" data-testid={`feed-item-${i}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center text-white font-display text-sm">
                      {(item.user_name || "?")[0].toUpperCase()}
                    </div>
                    <span className="text-white font-body text-sm font-medium">{item.user_name}</span>
                    <span className="text-white/30 text-xs font-body ml-auto">
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {item.content?.wise_saying && (
                    <p className="text-white/80 font-body text-sm italic pl-10">"{item.content.wise_saying}"</p>
                  )}
                  {item.content?.headline && (
                    <p className="text-white/60 font-body text-sm pl-10">{item.content.headline}</p>
                  )}
                  {item.content?.quote && (
                    <p className="text-[#D4AF37]/80 font-body text-sm italic pl-10">"{item.content.quote}"</p>
                  )}
                  <div className="flex items-center gap-3 pl-10">
                    <span className="text-white/30 text-xs font-body">{item.views} views</span>
                    <span className="text-white/20 text-xs">{item.share_type}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Compatibility Dialog */}
      <Dialog open={!!compatDialog} onOpenChange={() => setCompatDialog(null)}>
        <DialogContent className="bg-[#0A101D] border-white/10 text-white max-w-md max-h-[85vh] overflow-y-auto" aria-describedby="compat-desc">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              Cosmic Compatibility
            </DialogTitle>
          </DialogHeader>
          <p id="compat-desc" className="sr-only">Compatibility analysis between you and your friend</p>
          {compatLoading ? (
            <div className="flex flex-col items-center py-12 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
              <p className="text-white/60 font-body text-sm">Consulting the stars...</p>
            </div>
          ) : compatDialog && !compatDialog.loading ? (
            <div className="space-y-5 mt-2">
              {/* Names */}
              <div className="text-center">
                <p className="text-white font-body">
                  <span className="text-[#C4B5FD]">{compatDialog.user_name}</span>
                  <span className="text-white/30 mx-2">&</span>
                  <span className="text-[#D4AF37]">{compatDialog.friend_name}</span>
                </p>
              </div>

              {/* Score */}
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full border-4 border-[#D4AF37]/40 bg-[#D4AF37]/5">
                  <span className="font-display text-3xl text-[#D4AF37]">{compatDialog.overall_score}%</span>
                </div>
                <p className="text-white/50 font-body text-sm mt-2">{compatDialog.element_match}</p>
              </div>

              {/* Cosmic Bond */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className="text-[#C4B5FD] text-xs uppercase tracking-wider font-body mb-2">Cosmic Bond</p>
                <p className="text-white/80 font-body text-sm">{compatDialog.cosmic_bond}</p>
              </div>

              {/* Strengths */}
              <div>
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-body mb-2">Strengths</p>
                <div className="space-y-1.5">
                  {compatDialog.strengths?.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Star className="w-3.5 h-3.5 text-[#D4AF37]" />
                      <span className="text-white/70 font-body text-sm">{s}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Growth Areas */}
              <div>
                <p className="text-[#8B5CF6] text-xs uppercase tracking-wider font-body mb-2">Growth Areas</p>
                <div className="space-y-1.5">
                  {compatDialog.growth_areas?.map((a, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Heart className="w-3.5 h-3.5 text-[#8B5CF6]" />
                      <span className="text-white/70 font-body text-sm">{a}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Spiritual Synergy */}
              <div className="bg-[#8B5CF6]/5 rounded-xl p-4 border border-[#8B5CF6]/20">
                <p className="text-[#C4B5FD] text-xs uppercase tracking-wider font-body mb-2">Spiritual Synergy</p>
                <p className="text-white/80 font-body text-sm">{compatDialog.spiritual_synergy}</p>
              </div>

              {/* Advice */}
              <div className="border-t border-white/5 pt-4">
                <p className="text-[#D4AF37]/80 font-body text-sm italic text-center">{compatDialog.advice}</p>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FriendsPage;
